import { Injectable, inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ILabelPrinter } from '@shared/models';
import {
  CreateDeviceRelationshipCommand,
  CreateLabelPrinterDeviceCommand,
  CreatePrintKioskDeviceCommand,
  UpdateLabelPrinterCommand,
} from '@shared/models/commands';
import { KioskDeviceDto, PrintKiosk } from '@shared/models/print-kiosk.model';
import { KioskContextService } from '@core/services/kiosk-context.service';

import { AbstractApiServiceBase } from './abstract-data.service';

/** Matches API enum ProjectFalcon.Inventory.Core.Devices.DeviceHealthStatus (JSON number). */
export const enum DeviceHealthStatusApi {
  Unknown = 0,
  Healthy = 1,
  Degraded = 2,
  Unhealthy = 3
}

export function deviceHealthStatusLabel(
  status: DeviceHealthStatusApi | number | undefined | null
): string {
  switch (status ?? DeviceHealthStatusApi.Unknown) {
    case DeviceHealthStatusApi.Healthy:
      return 'Healthy';
    case DeviceHealthStatusApi.Degraded:
      return 'Degraded';
    case DeviceHealthStatusApi.Unhealthy:
      return 'Unhealthy';
    default:
      return 'Unknown';
  }
}

/** Parsed heartbeat payload (camelCase from API). */
export interface DeviceHealthPayloadFieldDto {
  key: string;
  value: string;
}

export interface DeviceHealthPayloadDetailsDto {
  schemaVersion: number;
  summaryLabel: string;
  fields: DeviceHealthPayloadFieldDto[];
}

/** Single health report row (camelCase from API). */
export interface DeviceHealthReportDto {
  id: string;
  reportedAt: string;
  healthStatus: DeviceHealthStatusApi;
  schemaVersion: number;
  dataJson?: string | null;
  receivedAt: string;
  /** Structured payload when the API could parse stored JSON. */
  payloadDetails?: DeviceHealthPayloadDetailsDto | null;
}

/** Device as returned by GET devices/all or GET devices/{id} (camelCase from API). */
export interface Device {
  id: string;
  name: string;
  readableId: string;
  deviceType: string;
  createdOn?: string;
  createdBy?: string | null;
  validFrom?: string;
  validTo?: string | null;
  metadata?: string | null;
  metadataSchemaVer?: number;
  /** Latest health; omitted or Unknown when never reported. */
  overallHealthStatus?: DeviceHealthStatusApi;
  /** Newest first; up to 25 on GET device/{id}; empty on list endpoints. */
  recentHealthReports?: DeviceHealthReportDto[];
  /** Populated when fetching a single device via getDeviceWithRelated (one level). */
  relatedDevices?: Device[];
}

/** Response from GET devices/graph: devices and explicit relationship pairs for edges. */
export interface GraphResponse {
  devices: Device[];
  relationships: Array<{ fromDeviceId: string; toDeviceId: string }>;
}

interface LabelPrinterMetadata {
  ipAddress?: string | null;
  physicalLocation?: string | null;
  defaultLabelTemplateName?: string | null;
  printService?: number;
  isPrimary?: boolean;
}

function parseDeviceMetadataJson<T extends object>(metadata: string | null | undefined): T {
  if (!metadata) return {} as T;
  try {
    return JSON.parse(metadata) as T;
  } catch {
    return {} as T;
  }
}

export function deviceToLabelPrinter(d: Device): ILabelPrinter {
  const meta = parseDeviceMetadataJson<LabelPrinterMetadata>(d.metadata);
  return {
    id: d.id,
    name: d.name,
    host: meta.ipAddress ?? '',
    physicalLocation: meta.physicalLocation ?? null,
    printService: meta.printService ?? 0,
    templateName: meta.defaultLabelTemplateName ?? null,
    isPrimary: meta.isPrimary ?? false,
  };
}

export function deviceToPrintKiosk(d: Device): PrintKiosk {
  const meta = parseDeviceMetadataJson<{ physicalLocation?: string }>(d.metadata);
  return {
    deviceId: d.id,
    name: d.name,
    readableId: d.readableId,
    physicalLocation: meta.physicalLocation,
  };
}

@Injectable({ providedIn: 'root' })
export class DeviceDataService extends AbstractApiServiceBase {
  private readonly _kioskContext = inject(KioskContextService);

  private get devicesBase(): string {
    return `${this.getBaseApiUrl('v1')}/devices`;
  }

  /**
   * Gets all devices, optionally filtered by device type(s).
   * When device context is configured (e.g. kiosk), adds X-Device-Id and X-Device-Key from context.
   * Otherwise relies on JWT (e.g. admin).
   */
  getAll(deviceTypes?: string[]): Observable<Device[]> {
    let url = `${this.devicesBase}/all`;
    if (deviceTypes?.length) {
      const params = deviceTypes
        .filter((t) => t?.trim())
        .map((t) => `deviceType=${encodeURIComponent(t.trim())}`)
        .join('&');
      if (params) url += `?${params}`;
    }
    const headers = this._deviceAuthHeaders();
    return this.http.get<Device[]>(url, { headers });
  }

  /**
   * Device graph: GET devices/graph.
   * Returns all devices plus explicit relationship pairs (from DeviceRelationships).
   * Use this for the device graph UI so edges reflect only actual relationships.
   */
  getGraph(): Observable<GraphResponse> {
    const url = `${this.devicesBase}/graph`;
    const headers = this._deviceAuthHeaders();
    return this.http.get<GraphResponse>(url, { headers });
  }

  /**
   * Gets a single device by ID with relatedDevices populated (one edge deep).
   */
  getDeviceWithRelated(deviceId: string): Observable<Device> {
    const url = `${this.devicesBase}/${deviceId}`;
    const headers = this._deviceAuthHeaders();
    return this.http.get<Device>(url, { headers });
  }

  getAllLabelPrinters(): Observable<ILabelPrinter[]> {
    return this.getAll(['LabelPrinter']).pipe(map((devices) => devices.map(deviceToLabelPrinter)));
  }

  /**
   * Get single label printer by device id. Requires x-device-key for that printer (device-auth). Not used from kiosk.
   */
  getLabelPrinter(id: string): Observable<ILabelPrinter> {
    const url = `${this.devicesBase}/label-printers/${id}`;
    return this.http.get<ILabelPrinter>(url, { headers: this._deviceAuthHeaders() });
  }

  addLabelPrinter(command: CreateLabelPrinterDeviceCommand): Observable<Device> {
    const url = `${this.devicesBase}/label-printer`;
    return this.http.post<Device>(url, command, { headers: this._deviceAuthHeaders() });
  }

  /**
   * Updates label printer name and/or metadata (IP as `host` or `ipAddress`, `physicalLocation`,
   * `printService`, `isPrimary`). Omitted body fields are left unchanged. PUT `devices/label-printer/{id}`.
   */
  updateLabelPrinter(id: string, command: UpdateLabelPrinterCommand): Observable<ILabelPrinter> {
    const url = `${this.devicesBase}/label-printer/${id}`;
    return this.http
      .put<Device>(url, command, { headers: this._deviceAuthHeaders() })
      .pipe(map((d) => deviceToLabelPrinter(d)));
  }

  /**
   * Get single kiosk by device id. Requires X-Device-Id and X-Device-Key (e.g. kiosk refresh on kiosk route).
   */
  getPrintKiosk(deviceId: string): Observable<KioskDeviceDto> {
    const url = `${this.devicesBase}/print-kiosks/${deviceId}`;
    return this.http.get<KioskDeviceDto>(url, { headers: this._deviceAuthHeaders() });
  }

  getAllPrintKiosks(): Observable<PrintKiosk[]> {
    return this.getAll(['PrintKiosk']).pipe(map((devices) => devices.map(deviceToPrintKiosk)));
  }

  addPrintKiosk(command: CreatePrintKioskDeviceCommand): Observable<Device> {
    const url = `${this.devicesBase}/kiosk`;
    return this.http.post<Device>(url, command, { headers: this._deviceAuthHeaders() });
  }

  /**
   * Create a device relationship (e.g. kiosk Hosts printer). Use relationshipType Hosts for kiosk–printer link.
   */
  createDeviceRelationship(command: CreateDeviceRelationshipCommand): Observable<unknown> {
    const url = `${this.devicesBase}/create-relationship`;
    return this.http.post<unknown>(url, command, { headers: this._deviceAuthHeaders() });
  }

  deleteDevice(id: string): Observable<void> {
    return this.http.delete<void>(`${this.devicesBase}/${id}`, { headers: this._deviceAuthHeaders() });
  }

  /**
   * Builds headers including X-Device-Id and X-Device-Key when device context is configured.
   */
  private _deviceAuthHeaders(): HttpHeaders {
    const base = this.getJsonHeader();
    const context = this._kioskContext.deviceContext();
    const deviceId = context?.kioskDevice?.deviceId;
    const deviceKey = context?.deviceKey ?? this._kioskContext.deviceKey() ?? null;
    if (deviceId && deviceKey) {
      return base
        .set('X-Device-Id', deviceId)
        .set('X-Device-Key', deviceKey);
    }
    return base;
  }
}
